import type { TableRow } from '../table-store';

// PSTUDIO perf — precomputed row-position indexes.
//
// The position getters (getRowIndex / getSortedRowIndex / getRowDisplayNumber)
// used to each run `getAllSortedRows().filter(...).findIndex(id)` — an O(N) scan
// PER CALL. They are read once per visible row/cell, so a render of ~50 rows on
// a 5,000-node workspace did ~50 × O(N) work, re-triggered by any row edit.
//
// These builders produce the full id→position index in a single O(N) pass, so
// the getters become O(1) map lookups reading one shared computed. The values
// are byte-identical to the old findIndex logic (proven in row-index-maps.test).

/**
 * 0-based position of every VISIBLE row (`hidden !== true`) in sorted order.
 * Equivalent to `sortedRows.filter((r) => r.hidden !== true).findIndex(byId)`.
 * Missing / hidden ids are absent → callers default to -1 (findIndex's "not
 * found").
 */
export function buildVisibleRowIndexMap(
  sortedRows: TableRow[]
): Map<string, number> {
  const map = new Map<string, number>();
  let index = 0;
  for (const row of sortedRows) {
    if (row.hidden === true) continue;
    // First occurrence wins, mirroring findIndex (ids are unique in practice).
    if (!map.has(row.id)) map.set(row.id, index);
    index += 1;
  }
  return map;
}

/**
 * 1-based visual number of every NON-group row in sorted order — group headers
 * are skipped so they don't consume a position (a header is a separator, not a
 * node). Equivalent to `sortedRows.filter((r) => !isGroupRow(r)).findIndex(byId)
 * + 1`. Hidden rows are deliberately NOT filtered (collapsing a group must not
 * renumber the rows below it). A group row / unknown id is absent → callers
 * default to 0 (findIndex -1, + 1).
 */
export function buildDisplayNumberMap(
  sortedRows: TableRow[],
  isGroupRow: (row: TableRow) => boolean
): Map<string, number> {
  const map = new Map<string, number>();
  let position = 0;
  for (const row of sortedRows) {
    if (isGroupRow(row)) continue;
    if (!map.has(row.id)) map.set(row.id, position + 1);
    position += 1;
  }
  return map;
}
